// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, Pin, PinOff, Shield } from 'lucide-react';
import { CoinHeaderCell, StandardHeaderCell, getTableHeaderCellClasses, getStickyPositionStyle } from './TableHeaderCells';
import { ColumnTooltip } from './ColumnTooltip';
import CoinLogoImage from './CoinLogoImage';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';

interface HedgePerformerETH {
  rank: number;
  coin_id: string;
  symbol: string;
  name: string;
  logo_url?: string;
  avg_return_eth_down: number;
  median_return_eth_down: number;
  hit_rate: number;
  correlation_with_eth: number;
  volatility: number;
  hedge_score: number;
  eth_down_days_count: number;
  positive_days_count: number;
  current_price: number;
  volume_24h: number;
  data_date: string;
}

interface HedgePerformersETHData {
  summary: {
    total_analyzed: number;
    positive_performers: number;
    avg_hedge_score: number;
    timeframe: string;
    insight: string;
  };
  performers: HedgePerformerETH[];
  timestamp: string;
}

interface HedgePerformersETHProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

const HedgePerformersETH: React.FC<HedgePerformersETHProps> = ({ isOpen: isOpenProp, onToggle: onToggleProp }) => {
  const { isDark } = useTheme();
  const [internalIsOpen, setInternalIsOpen] = useState(true);

  // Use internal state if no external control is provided
  const isOpen = isOpenProp !== undefined ? isOpenProp : internalIsOpen;
  const handleToggle = onToggleProp || (() => setInternalIsOpen(!internalIsOpen));

  const [data, setData] = useState<HedgePerformersETHData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTimeframe, setActiveTimeframe] = useState<'1Y' | '1M'>('1Y');
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Mock data for fallback when API fails
  const mockData: HedgePerformersETHData = {
    summary: {
      total_analyzed: 100,
      positive_performers: 18,
      avg_hedge_score: 0.142,
      timeframe: "1Y",
      insight: "During ETH downturns, stablecoins and DeFi blue-chips show moderate hedge characteristics, with 18% of analyzed assets providing positive returns when Ethereum falls."
    },
    performers: [
      {
        rank: 1,
        coin_id: "tether",
        symbol: "USDT",
        name: "Tether",
        avg_return_eth_down: 0.15,
        median_return_eth_down: 0.11,
        hit_rate: 89.2,
        correlation_with_eth: -0.08,
        volatility: 0.91,
        hedge_score: 0.89,
        eth_down_days_count: 156,
        positive_days_count: 139,
        current_price: 1.001,
        volume_24h: 24780000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 2,
        coin_id: "usd-coin",
        symbol: "USDC",
        name: "USD Coin",
        avg_return_eth_down: 0.09,
        median_return_eth_down: 0.06,
        hit_rate: 86.5,
        correlation_with_eth: -0.04,
        volatility: 0.78,
        hedge_score: 0.83,
        eth_down_days_count: 156,
        positive_days_count: 135,
        current_price: 0.9998,
        volume_24h: 5670000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 3,
        coin_id: "maker",
        symbol: "MKR",
        name: "Maker",
        avg_return_eth_down: 0.67,
        median_return_eth_down: 0.34,
        hit_rate: 52.6,
        correlation_with_eth: 0.15,
        volatility: 8.9,
        hedge_score: 0.45,
        eth_down_days_count: 156,
        positive_days_count: 82,
        current_price: 1234.56,
        volume_24h: 89700000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 4,
        coin_id: "compound-governance-token",
        symbol: "COMP",
        name: "Compound",
        avg_return_eth_down: 0.45,
        median_return_eth_down: 0.28,
        hit_rate: 48.7,
        correlation_with_eth: 0.34,
        volatility: 9.8,
        hedge_score: 0.38,
        eth_down_days_count: 156,
        positive_days_count: 76,
        current_price: 56.78,
        volume_24h: 23400000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 5,
        coin_id: "aave",
        symbol: "AAVE",
        name: "Aave",
        avg_return_eth_down: 0.23,
        median_return_eth_down: 0.15,
        hit_rate: 45.5,
        correlation_with_eth: 0.56,
        volatility: 11.2,
        hedge_score: 0.32,
        eth_down_days_count: 156,
        positive_days_count: 71,
        current_price: 89.45,
        volume_24h: 156000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 6,
        coin_id: "bitcoin",
        symbol: "BTC",
        name: "Bitcoin",
        avg_return_eth_down: 0.12,
        median_return_eth_down: 0.08,
        hit_rate: 43.6,
        correlation_with_eth: 0.72,
        volatility: 4.2,
        hedge_score: 0.29,
        eth_down_days_count: 156,
        positive_days_count: 68,
        current_price: 67432.21,
        volume_24h: 28450000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 7,
        coin_id: "chainlink",
        symbol: "LINK",
        name: "Chainlink",
        avg_return_eth_down: -0.12,
        median_return_eth_down: -0.08,
        hit_rate: 41.0,
        correlation_with_eth: 0.78,
        volatility: 8.9,
        hedge_score: 0.25,
        eth_down_days_count: 156,
        positive_days_count: 64,
        current_price: 11.23,
        volume_24h: 425000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 8,
        coin_id: "polygon",
        symbol: "MATIC",
        name: "Polygon",
        avg_return_eth_down: -0.34,
        median_return_eth_down: -0.26,
        hit_rate: 38.5,
        correlation_with_eth: 0.81,
        volatility: 9.5,
        hedge_score: 0.21,
        eth_down_days_count: 156,
        positive_days_count: 60,
        current_price: 0.387,
        volume_24h: 298000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 9,
        coin_id: "cardano",
        symbol: "ADA",
        name: "Cardano",
        avg_return_eth_down: -0.56,
        median_return_eth_down: -0.43,
        hit_rate: 35.9,
        correlation_with_eth: 0.85,
        volatility: 9.1,
        hedge_score: 0.18,
        eth_down_days_count: 156,
        positive_days_count: 56,
        current_price: 0.356,
        volume_24h: 267000000,
        data_date: new Date().toISOString().split('T')[0]
      },
      {
        rank: 10,
        coin_id: "solana",
        symbol: "SOL",
        name: "Solana",
        avg_return_eth_down: -0.78,
        median_return_eth_down: -0.59,
        hit_rate: 32.7,
        correlation_with_eth: 0.87,
        volatility: 12.8,
        hedge_score: 0.14,
        eth_down_days_count: 156,
        positive_days_count: 51,
        current_price: 98.45,
        volume_24h: 2847000000,
        data_date: new Date().toISOString().split('T')[0]
      }
    ],
    timestamp: new Date().toISOString()
  };

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<{[key: string]: boolean}>(() => {
    const saved = localStorage.getItem('hedgePerformersEthColumns');
    return saved ? JSON.parse(saved) : {
      rank: true,
      coin: true,
      hedgeScore: true,
      avgReturn: true,
      hitRate: true,
      correlation: true,
      volatility: true,
      downDays: true,
    };
  });

  // Pinned columns state
  const [pinnedColumns, setPinnedColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('hedgePerformersEthPinnedColumns');
    return saved ? JSON.parse(saved) : [];
  });

  // Header cell refs for width measurement
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});

  // Column widths state for sticky positioning
  const [colWidths, setColWidths] = useState<Record<string, number>>({});

  // Toast notification for pin limit
  const [showPinLimitToast, setShowPinLimitToast] = useState(false);


  useEffect(() => {
    // Only fetch if opened AND no data exists yet
    if (isOpen && !data) {
      fetchData(activeTimeframe);
    }
  }, [isOpen]);

  // Fetch when timeframe changes (regardless of isOpen state)
  useEffect(() => {
    if (data) {
      fetchData(activeTimeframe);
    }
  }, [activeTimeframe]);

  // Add horizontal scroll on wheel
  useEffect(() => {
    const scrollContainer = tableScrollRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e: WheelEvent) => {
      // Check if table has horizontal overflow
      const hasHorizontalScroll = scrollContainer.scrollWidth > scrollContainer.clientWidth;

      if (hasHorizontalScroll) {
        e.preventDefault();
        e.stopPropagation();

        // Convert vertical scroll to horizontal scroll
        const scrollAmount = e.deltaY;
        scrollContainer.scrollLeft += scrollAmount;
      }
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [data]); // Re-run when data changes

  // Measure column widths from header refs
  const measureColumnWidths = () => {
    const keys = ['rank', 'coin', 'hedgeScore', 'avgReturn', 'hitRate', 'correlation', 'volatility', 'downDays'] as const;
    const next: Record<string, number> = {};
    keys.forEach(k => {
      const el = headerRefs.current[k];
      if (el) {
        next[k] = el.getBoundingClientRect().width;
      }
    });
    setColWidths(next);
  };

  // Measure column widths on mount and when dependencies change
  useEffect(() => {
    measureColumnWidths();
    const ro = new ResizeObserver(() => measureColumnWidths());
    if (tableScrollRef.current) {
      ro.observe(tableScrollRef.current);
    }
    const handleResize = () => measureColumnWidths();
    window.addEventListener('resize', handleResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [visibleColumns, pinnedColumns, isDark]);

  type RawPerformer = any;

  function normalizePayload(raw: any): HedgePerformersETHData | null {
    if (!raw) return null;

    // Allow both { performers, summary } and { data: { performers, summary } }
    const root = raw.data?.performers ? raw.data : raw;

    // Accept alternative key names and map them into our interface
    const performers: HedgePerformerETH[] = Array.isArray(root.performers)
      ? root.performers.map((p: RawPerformer, i: number) => ({
          rank: Number(p.rank ?? i + 1),
          coin_id: p.coin_id ?? p.id ?? '',
          symbol: p.symbol ?? p.ticker ?? '',
          name: p.name ?? p.full_name ?? p.symbol ?? '',
          logo_url: p.logo_url ?? '',
          avg_return_eth_down: Number(p.avg_return_eth_down ?? p.avg_return_on_eth_down ?? p.avg_return ?? 0),
          median_return_eth_down: Number(p.median_return_eth_down ?? p.median_return ?? 0),
          hit_rate: Number(p.hit_rate ?? p.win_rate ?? 0),
          correlation_with_eth: Number(p.correlation_with_eth ?? p.eth_corr ?? p.corr ?? 0),
          volatility: Number(p.volatility ?? p.stdev ?? 0),
          hedge_score: Number(p.hedge_score ?? p.score ?? 0),
          eth_down_days_count: Number(p.eth_down_days_count ?? p.down_days ?? 0),
          positive_days_count: Number(p.positive_days_count ?? p.pos_days ?? 0),
          current_price: Number(p.current_price ?? p.price ?? 0),
          volume_24h: Number(p.volume_24h ?? p.volume ?? 0),
          data_date: String(p.data_date ?? p.date ?? ''),
        }))
      : [];

    const summary = root.summary ?? {};
    const normalized: HedgePerformersETHData = {
      summary: {
        total_analyzed: Number(summary.total_analyzed ?? performers.length ?? 0),
        positive_performers: Number(summary.positive_performers ?? performers.filter(x => x.avg_return_eth_down > 0).length),
        avg_hedge_score: Number(summary.avg_hedge_score ?? (performers.length ? performers.reduce((s, p) => s + (p.hedge_score || 0), 0) / performers.length : 0)),
        timeframe: String(summary.timeframe ?? '1Y'),
        insight: String(summary.insight ?? 'No data available'),
      },
      performers,
      timestamp: String(root.timestamp ?? new Date().toISOString()),
    };

    // If no performers, treat as invalid to preserve existing (mock) data
    if (!normalized.performers || normalized.performers.length === 0) return null;
    return normalized;
  }

  const fetchData = async (timeframe: '1Y' | '1M') => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) {
      setError('Authentication required. Please log in to view ETH hedge performers data.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/hedge-performers-eth?timeframe=${timeframe}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) throw new Error('Failed to fetch ETH hedge performers data');

      const raw = await response.json();
      const normalized = normalizePayload(raw);

      if (normalized) {
        setData(normalized);
      } else {
        console.warn('No API data available, using mock data fallback');
        setData(mockData);
      }
    } catch (err) {
      console.error('ETH Hedge: API error:', err);
      console.warn('API failed, using mock data fallback');
      setData(mockData);
      setError(null); // Clear error since we're showing mock data
    } finally {
      setLoading(false);
    }
  };

  const toggleColumn = (columnKey: string) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('hedgePerformersEthColumns', JSON.stringify(newVisibleColumns));
  };

  // Define natural column order for consistent pinning
  const PIN_ORDER = [
    'hedgeScore',
    'avgReturn',
    'hitRate',
    'correlation',
    'volatility',
    'downDays',
  ];

  // Helper function to sort pinned columns according to PIN_ORDER
  const sortPinnedColumns = (columns: string[]): string[] => {
    return PIN_ORDER.filter(col => columns.includes(col));
  };

  const togglePin = (columnKey: string) => {
    const isPinned = pinnedColumns.includes(columnKey);

    if (isPinned) {
      // Unpin column
      const filteredColumns = pinnedColumns.filter(col => col !== columnKey);
      const newPinnedColumns = sortPinnedColumns(filteredColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('hedgePerformersEthPinnedColumns', JSON.stringify(newPinnedColumns));
    } else {
      // Check limit
      if (pinnedColumns.length >= 6) {
        setShowPinLimitToast(true);
        setTimeout(() => setShowPinLimitToast(false), 3000);
        return;
      }

      // Pin column and sort according to PIN_ORDER
      const unsortedColumns = [...pinnedColumns, columnKey];
      const newPinnedColumns = sortPinnedColumns(unsortedColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('hedgePerformersEthPinnedColumns', JSON.stringify(newPinnedColumns));
    }
  };

  const getColumnLeftPosition = (columnKey: string): number => {
    // Ensure we work with the properly ordered pinned columns
    const orderedPinnedColumns = sortPinnedColumns(pinnedColumns);
    const columnIndex = orderedPinnedColumns.indexOf(columnKey);
    if (columnIndex === -1) return 0; // Not pinned

    // Start after the permanently sticky columns (rank + coin)
    let leftPosition = (colWidths.rank || 0) + (colWidths.coin || 0);

    // Add widths of pinned columns that come before this one in PIN_ORDER
    for (let i = 0; i < columnIndex; i++) {
      const prevColumn = orderedPinnedColumns[i];
      leftPosition += colWidths[prevColumn] || 0;
    }
    return leftPosition;
  };

  const formatPercentage = (value: number) => {
    const sign = value >= 0 ? '+' : '';
    let color = isDark ? 'text-gray-400' : 'text-gray-600';
    if (value > 0) color = isDark ? 'text-emerald-400' : 'text-green-600';
    else if (value < 0) color = isDark ? 'text-red-400' : 'text-red-600';

    return (
      <span className={color}>{sign}{value.toFixed(2)}%</span>
    );
  };

  const formatCorrelation = (value: number) => {
    const absValue = Math.abs(value);
    let color = isDark ? 'text-gray-400' : 'text-gray-600';

    if (absValue < 0.2) color = isDark ? 'text-emerald-400' : 'text-green-600'; // Good hedge
    else if (absValue < 0.5) color = isDark ? 'text-yellow-400' : 'text-yellow-600'; // Moderate
    else color = isDark ? 'text-red-400' : 'text-red-600'; // High correlation

    return (
      <span className={color}>{value.toFixed(3)}</span>
    );
  };

  const getHedgeScoreBadge = (score: number) => {
    if (isDark) {
      if (score >= 70) return 'bg-emerald-400/20 text-emerald-400 border-emerald-400/30';
      if (score >= 50) return 'bg-yellow-400/20 text-yellow-400 border-yellow-400/30';
      return 'bg-red-400/20 text-red-400 border-red-400/30';
    } else {
      if (score >= 70) return 'bg-green-100 text-green-800 border-green-200';
      if (score >= 50) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      return 'bg-red-100 text-red-800 border-red-200';
    }
  };

  const getResilienceBadge = (hitRate: number) => {
    if (hitRate >= 60) {
      return (
        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${
          isDark
            ? 'bg-emerald-400/20 text-emerald-400 border-emerald-400/30'
            : 'bg-green-100 text-green-800 border-green-200'
        }`}>
          🛡️
        </span>
      );
    }
    return null;
  };

  const timeframeLabels = {
    '1Y': '1 Year',
    '1M': '1 Month'
  };

  return (
    <div className={`rounded-2xl shadow-sm border mb-6 transition-all duration-200 ${
      isDark
        ? 'bg-white/[0.03] border-white/10'
        : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div
        className={`px-6 py-4 border-b cursor-pointer transition-colors duration-200 ${
          isDark
            ? 'border-white/10 hover:bg-white/5'
            : 'border-gray-200 hover:bg-gray-50'
        }`}
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
              <Shield className={`${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} size={24} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Hedge Performers — When ETH Falls
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                Top coins that performed well when Ethereum declined over different timeframes
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="group relative">
              <span className={`text-lg cursor-help ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>ℹ️</span>
              <div className={`absolute bottom-full right-0 mb-2 w-80 p-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 ${
                isDark ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
              }`}>
                <div className="font-medium mb-1">How Hedge Score is Calculated:</div>
                <div className="text-xs space-y-1">
                  <div>• <strong>Avg Return:</strong> Average % return during ETH down days (40% weight)</div>
                  <div>• <strong>Hit Rate:</strong> % of ETH down days coin was positive (30% weight)</div>
                  <div>• <strong>Correlation:</strong> Lower correlation with ETH is better (20% weight)</div>
                  <div>• <strong>Volatility:</strong> Lower volatility is better (10% weight)</div>
                </div>
              </div>
            </div>
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''} ${
                isDark ? 'text-gray-400' : 'text-gray-400'
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <div className={`transition-all duration-300 ease-in-out ${
        isOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
      }`}>
        {isOpen && (
          <div>
            {/* Timeframe Tabs */}
            <div className="px-6 pt-6">
              <div className={`flex space-x-1 mb-6 p-1 rounded-lg w-fit ${
                isDark ? 'bg-white/[0.05]' : 'bg-gray-100'
              }`}>
              {Object.entries(timeframeLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTimeframe(key as '1Y' | '1M')}
                  className={`px-6 py-2 rounded-md font-medium text-sm transition-all ${
                    activeTimeframe === key
                      ? isDark
                        ? 'bg-emerald-400/20 text-emerald-400 shadow-sm'
                        : 'bg-white text-gray-900 shadow-sm'
                      : isDark
                        ? 'text-gray-400 hover:text-white'
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {label}
                </button>
              ))}
              </div>
            </div>

            {loading ? (
              <div className="px-6">
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className={`animate-spin rounded-full h-12 w-12 border-b-2 mx-auto ${
                    isDark ? 'border-emerald-400' : 'border-blue-600'
                  }`}></div>
                  <p className={`mt-4 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading ETH hedge performers...</p>
                </div>
              </div>
              </div>
            ) : error ? (
              <div className="px-6">
              <div className="text-center py-12">
                <p className={isDark ? 'text-red-400' : 'text-red-600'}>{error}</p>
                <button
                  onClick={() => fetchData(activeTimeframe)}
                  className={`mt-4 px-4 py-2 rounded ${
                    isDark
                      ? 'bg-emerald-400/20 text-emerald-400 hover:bg-emerald-400/30'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Retry
                </button>
              </div>
              </div>
            ) : data ? (
              <>
                {/* Summary Card */}
                <div className="px-6">
                <div className={`rounded-lg p-4 mb-6 border ${
                  isDark
                    ? 'bg-purple-500/10 border-purple-400/20'
                    : 'bg-gradient-to-r from-purple-50 to-blue-50 border-purple-100'
                }`}>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Total Analyzed</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{data.summary.total_analyzed}</p>
                    </div>
                    <div>
                      <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Positive Performers</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-emerald-400' : 'text-green-600'}`}>{data.summary.positive_performers}</p>
                    </div>
                    <div>
                      <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Avg Hedge Score</p>
                      <p className={`text-2xl font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{data.summary.avg_hedge_score.toFixed(1)}</p>
                    </div>
                    <div className="md:col-span-1">
                      <p className={`text-xs mb-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Insight</p>
                      <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{data.summary.insight}</p>
                    </div>
                  </div>
                </div>
                </div>

                {/* Table */}
                <div
                  ref={tableScrollRef}
                  className={`overflow-x-auto custom-scrollbar ${!isDark ? 'light-scrollbar' : ''} relative max-h-none`}
                  title="Use mouse wheel to scroll horizontally"
                  style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: isDark ? '#4b5563 #1f2937' : '#d1d5db #f3f4f6'
                  }}
                >
                  <table className={`w-full text-sm ${isDark ? '' : ''}`}>
                    <thead className={`text-xs uppercase tracking-wide ${isDark ? 'opacity-80' : 'opacity-80'}`}>
                      <tr className={`h-24 border-b ${isDark ? 'border-white/5' : 'border-gray-200'}`}>
                        <th
                          ref={(el) => (headerRefs.current.rank = el)}
                          className={`h-24 text-center align-top sticky left-0 z-10 bg-inherit`}
                          style={{ width: 'max-content', minWidth: 'max-content', position: 'sticky', left: 0, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }}
                        >
                          <CoinHeaderCell title="Rank" />
                        </th>
                        <th
                          ref={(el) => (headerRefs.current.coin = el)}
                          className={`h-24 text-center align-top sticky z-10 bg-inherit`}
                          style={{ width: 'max-content', minWidth: 'max-content', position: 'sticky', left: '80px', top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }}
                        >
                          <CoinHeaderCell title="Coin" />
                        </th>
                        <th
                          ref={(el) => (headerRefs.current.hedgeScore = el)}
                          className={getTableHeaderCellClasses(visibleColumns.hedgeScore, pinnedColumns.includes('hedgeScore'), isDark)}
                          style={getStickyPositionStyle(pinnedColumns.includes('hedgeScore'), getColumnLeftPosition('hedgeScore'), isDark)}
                        >
                          <StandardHeaderCell
                            colKey="hedgeScore"
                            label="Hedge Score"
                            abbrev="HS"
                            visible={visibleColumns.hedgeScore}
                            pinned={pinnedColumns.includes('hedgeScore')}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        </th>
                        <th
                          ref={(el) => (headerRefs.current.avgReturn = el)}
                          className={getTableHeaderCellClasses(visibleColumns.avgReturn, pinnedColumns.includes('avgReturn'), isDark)}
                          style={getStickyPositionStyle(pinnedColumns.includes('avgReturn'), getColumnLeftPosition('avgReturn'), isDark)}
                        >
                          <StandardHeaderCell
                            colKey="avgReturn"
                            label="Avg Return"
                            abbrev="Avg"
                            visible={visibleColumns.avgReturn}
                            pinned={pinnedColumns.includes('avgReturn')}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        </th>
                        <th
                          ref={(el) => (headerRefs.current.hitRate = el)}
                          className={getTableHeaderCellClasses(visibleColumns.hitRate, pinnedColumns.includes('hitRate'), isDark)}
                          style={getStickyPositionStyle(pinnedColumns.includes('hitRate'), getColumnLeftPosition('hitRate'), isDark)}
                        >
                          <StandardHeaderCell
                            colKey="hitRate"
                            label="Hit Rate"
                            abbrev="Hit"
                            visible={visibleColumns.hitRate}
                            pinned={pinnedColumns.includes('hitRate')}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        </th>
                        <th
                          ref={(el) => (headerRefs.current.correlation = el)}
                          className={getTableHeaderCellClasses(visibleColumns.correlation, pinnedColumns.includes('correlation'), isDark)}
                          style={getStickyPositionStyle(pinnedColumns.includes('correlation'), getColumnLeftPosition('correlation'), isDark)}
                        >
                          <StandardHeaderCell
                            colKey="correlation"
                            label="ETH Corr."
                            abbrev="Corr"
                            visible={visibleColumns.correlation}
                            pinned={pinnedColumns.includes('correlation')}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        </th>
                        <th
                          ref={(el) => (headerRefs.current.volatility = el)}
                          className={getTableHeaderCellClasses(visibleColumns.volatility, pinnedColumns.includes('volatility'), isDark)}
                          style={getStickyPositionStyle(pinnedColumns.includes('volatility'), getColumnLeftPosition('volatility'), isDark)}
                        >
                          <StandardHeaderCell
                            colKey="volatility"
                            label="Volatility"
                            abbrev="Vol"
                            visible={visibleColumns.volatility}
                            pinned={pinnedColumns.includes('volatility')}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        </th>
                        <th
                          ref={(el) => (headerRefs.current.downDays = el)}
                          className={getTableHeaderCellClasses(visibleColumns.downDays, pinnedColumns.includes('downDays'), isDark)}
                          style={getStickyPositionStyle(pinnedColumns.includes('downDays'), getColumnLeftPosition('downDays'), isDark)}
                        >
                          <StandardHeaderCell
                            colKey="downDays"
                            label="ETH Down Days"
                            abbrev="Days"
                            visible={visibleColumns.downDays}
                            pinned={pinnedColumns.includes('downDays')}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.performers.map((performer) => (
                        <tr key={performer.rank} className={`transition-colors ${isDark ? 'hover:bg-white/[0.05]' : 'hover:bg-gray-50'}`}>
                          <td
                            className={`py-2 text-center transition-all duration-300 ${
                              visibleColumns.rank
                                ? 'px-6'
                                : 'w-8 max-w-8'
                            } text-sm font-medium sticky left-0 z-10 ${isDark ? 'text-white' : 'text-gray-900'}`}
                            style={{ position: 'sticky', left: 0, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }}
                          >
                            {visibleColumns.rank ? (
                              <div>#{performer.rank}</div>
                            ) : (
                              <ColumnTooltip
                                content={<div>#{performer.rank}</div>}
                                onColumnClick={() => toggleColumn('rank')}
                              >
                                <div className="py-1.5">
                                  <div className="text-xs opacity-50">•</div>
                                </div>
                              </ColumnTooltip>
                            )}
                          </td>
                          <td
                            className={`py-2 text-center transition-all duration-300 ${
                              visibleColumns.coin
                                ? 'px-6'
                                : 'w-8 max-w-8'
                            } sticky z-10`}
                            style={{ position: 'sticky', left: `${colWidths.rank || 0}px`, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }}
                          >
                            {visibleColumns.coin ? (
                              <div className="flex items-center">
                                <CoinLogoImage
                                  src={performer.logo_url || ''}
                                  alt={performer.symbol}
                                  size="md"
                                  className="mr-3"
                                />
                                <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{performer.symbol}</div>
                              </div>
                            ) : (
                              <ColumnTooltip
                                content={
                                  <div className="flex items-center">
                                    <CoinLogoImage
                                      src={performer.logo_url || ''}
                                      alt={performer.symbol}
                                      size="md"
                                      className="mr-3"
                                    />
                                    <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{performer.symbol}</div>
                                  </div>
                                }
                                onColumnClick={() => toggleColumn('coin')}
                              >
                                <div className="py-1.5">
                                  <div className="text-xs opacity-50">•</div>
                                </div>
                              </ColumnTooltip>
                            )}
                          </td>
                          <td
                            className={`py-2 text-center transition-all duration-300 ${
                              visibleColumns.hedgeScore
                                ? 'px-6'
                                : 'w-8 max-w-8'
                            } ${pinnedColumns.includes('hedgeScore') ? 'sticky' : ''}`}
                            style={pinnedColumns.includes('hedgeScore')
                              ? { position: 'sticky', left: `${getColumnLeftPosition('hedgeScore')}px`, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }
                              : undefined}
                          >
                            {visibleColumns.hedgeScore ? (
                              <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getHedgeScoreBadge(performer.hedge_score)}`}>
                                {performer.hedge_score.toFixed(1)}
                              </span>
                            ) : (
                              <ColumnTooltip
                                content={
                                  <span className={`px-3 py-1 rounded-full text-sm font-bold border ${getHedgeScoreBadge(performer.hedge_score)}`}>
                                    {performer.hedge_score.toFixed(1)}
                                  </span>
                                }
                                onColumnClick={() => toggleColumn('hedgeScore')}
                              >
                                <div className="py-1.5">
                                  <div className="text-xs opacity-50">•</div>
                                </div>
                              </ColumnTooltip>
                            )}
                          </td>
                          <td
                            className={`py-2 text-center transition-all duration-300 ${
                              visibleColumns.avgReturn
                                ? 'px-6'
                                : 'w-8 max-w-8'
                            } ${pinnedColumns.includes('avgReturn') ? 'sticky' : ''}`}
                            style={pinnedColumns.includes('avgReturn')
                              ? { position: 'sticky', left: `${getColumnLeftPosition('avgReturn')}px`, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }
                              : undefined}
                          >
                            {visibleColumns.avgReturn ? (
                              formatPercentage(performer.avg_return_eth_down)
                            ) : (
                              <ColumnTooltip
                                content={formatPercentage(performer.avg_return_eth_down)}
                                onColumnClick={() => toggleColumn('avgReturn')}
                              >
                                <div className="py-1.5">
                                  <div className="text-xs opacity-50">•</div>
                                </div>
                              </ColumnTooltip>
                            )}
                          </td>
                          <td
                            className={`py-2 text-center transition-all duration-300 ${
                              visibleColumns.hitRate
                                ? 'px-6'
                                : 'w-8 max-w-8'
                            } ${pinnedColumns.includes('hitRate') ? 'sticky' : ''}`}
                            style={pinnedColumns.includes('hitRate')
                              ? { position: 'sticky', left: `${getColumnLeftPosition('hitRate')}px`, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }
                              : undefined}
                          >
                            {visibleColumns.hitRate ? (
                              <div className="flex flex-col items-center">
                                <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{performer.hit_rate.toFixed(1)}%</span>
                                {getResilienceBadge(performer.hit_rate)}
                              </div>
                            ) : (
                              <ColumnTooltip
                                content={
                                  <div className="flex flex-col items-center">
                                    <span className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{performer.hit_rate.toFixed(1)}%</span>
                                    {getResilienceBadge(performer.hit_rate)}
                                  </div>
                                }
                                onColumnClick={() => toggleColumn('hitRate')}
                              >
                                <div className="py-1.5">
                                  <div className="text-xs opacity-50">•</div>
                                </div>
                              </ColumnTooltip>
                            )}
                          </td>
                          <td
                            className={`py-2 text-center transition-all duration-300 ${
                              visibleColumns.correlation
                                ? 'px-6'
                                : 'w-8 max-w-8'
                            } ${pinnedColumns.includes('correlation') ? 'sticky' : ''}`}
                            style={pinnedColumns.includes('correlation')
                              ? { position: 'sticky', left: `${getColumnLeftPosition('correlation')}px`, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }
                              : undefined}
                          >
                            {visibleColumns.correlation ? (
                              formatCorrelation(performer.correlation_with_eth)
                            ) : (
                              <ColumnTooltip
                                content={formatCorrelation(performer.correlation_with_eth)}
                                onColumnClick={() => toggleColumn('correlation')}
                              >
                                <div className="py-1.5">
                                  <div className="text-xs opacity-50">•</div>
                                </div>
                              </ColumnTooltip>
                            )}
                          </td>
                          <td
                            className={`py-2 text-center transition-all duration-300 ${
                              visibleColumns.volatility
                                ? 'px-6'
                                : 'w-8 max-w-8'
                            } ${pinnedColumns.includes('volatility') ? 'sticky' : ''} ${isDark ? 'text-gray-400' : 'text-gray-600'}`}
                            style={pinnedColumns.includes('volatility')
                              ? { position: 'sticky', left: `${getColumnLeftPosition('volatility')}px`, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }
                              : undefined}
                          >
                            {visibleColumns.volatility ? (
                              <span>{performer.volatility.toFixed(2)}%</span>
                            ) : (
                              <ColumnTooltip
                                content={<span>{performer.volatility.toFixed(2)}%</span>}
                                onColumnClick={() => toggleColumn('volatility')}
                              >
                                <div className="py-1.5">
                                  <div className="text-xs opacity-50">•</div>
                                </div>
                              </ColumnTooltip>
                            )}
                          </td>
                          <td
                            className={`py-2 text-center transition-all duration-300 ${
                              visibleColumns.downDays
                                ? 'px-6'
                                : 'w-8 max-w-8'
                            } ${pinnedColumns.includes('downDays') ? 'sticky' : ''}`}
                            style={pinnedColumns.includes('downDays')
                              ? { position: 'sticky', left: `${getColumnLeftPosition('downDays')}px`, top: 0, zIndex: 2, background: isDark ? '#09090b' : '#ffffff' }
                              : undefined}
                          >
                            {visibleColumns.downDays ? (
                              <div className="text-sm">
                                <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{performer.eth_down_days_count}</div>
                                <div className={`text-xs ${isDark ? 'text-emerald-400' : 'text-green-600'}`}>{performer.positive_days_count} positive</div>
                              </div>
                            ) : (
                              <ColumnTooltip
                                content={
                                  <div className="text-sm">
                                    <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{performer.eth_down_days_count}</div>
                                    <div className={`text-xs ${isDark ? 'text-emerald-400' : 'text-green-600'}`}>{performer.positive_days_count} positive</div>
                                  </div>
                                }
                                onColumnClick={() => toggleColumn('downDays')}
                              >
                                <div className="py-1.5">
                                  <div className="text-xs opacity-50">•</div>
                                </div>
                              </ColumnTooltip>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            ) : null}
          </div>
        )}
      </div>

      {/* Toast notification for pin limit */}
      {showPinLimitToast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 ${
            isDark
              ? 'bg-red-500/90 text-white border border-red-400'
              : 'bg-red-100 text-red-900 border border-red-300'
          }`}
          role="alert"
        >
          Limit reached (6). Unpin another column to continue.
        </div>
      )}
    </div>
  );
};

export default HedgePerformersETH;
